import {
  Component, Input, ElementRef, AfterViewInit, ViewChild, EventEmitter, Output, OnChanges, SimpleChanges, OnDestroy
} from '@angular/core';

import {Project} from '../../shared/models/project';
import {Size} from '../../shared/models/size';
import {Opening, OpeningWithPosition} from '../../shared/models/opening';
import {YesNoDialogComponent} from '../../yesno-dialog/yesno-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {environment} from '../../../environments/environment';
import {SizeRange} from '../../shared/models/sizeRange';
import {Shape} from '../../shared/models/shape';
import {ShapeSelectorComponent} from '../shape-selector/shape-selector.component';
import {RotationViolationDialogComponent} from '../rotation-violation-dialog/rotation-violation-dialog.component';
import {ZoomDialogComponent} from '../zoom-dialog/zoom-dialog.component';
import {Subscription} from "rxjs";

@Component({
  selector: 'mbnm-project-preview',
  templateUrl: './project-preview.component.html',
  styleUrls: ['./project-preview.component.scss']
})
export class ProjectPreviewComponent implements AfterViewInit, OnChanges, OnDestroy  {

  private projectChangeSubRef: Subscription;
  @Input()
  public showRotationRestrictions = false

  public editingVGroove = false;
  @ViewChild('shapeSelector') shapeSelector: ShapeSelectorComponent;

  constructor(public dialog: MatDialog) {
  }

  round(val):number {
    const returnVal = Math.floor(val / 50) * 50;
    if (returnVal >= 300) {
      return returnVal;
    }
    return 300;
  }

  private get size():Size {
    if (this.project.size.incomplete) {
      return Project.DEFAULTS.outer;
    } else {
      return this.project.size;
    }
  }

  public get previewPosition() {
    const size = this.size
    let h, w;
    const mouldingWidth = (this.project.moulding ? this.project.moulding.width * 2 : 0);
    if (size.width < size.height) {
      h = 100;
      w = (size.width + mouldingWidth) / (size.height + mouldingWidth) * h + 1;
    } else {
      w = 90;
      h = (size.height + mouldingWidth) / (size.width + mouldingWidth) * w;
    }

    return {
      h: h + '%',
      w: w + '%',
      l: ((100 - w) / 2)  + '%',
      t: (100 - h) / 2  + '%'
    };
  }

  public outerPosition: any;
  public openingsWithPosition: Array<OpeningWithPosition>;

  public updateOuterPosition(): void {
    let size = this.size;
    let mouldingWidth = this.project.moulding ? this.project.moulding.width : 0;
    let w = size.width / (size.width + 2 * mouldingWidth) * 100;
    let h = size.height / (size.height + 2 * mouldingWidth) * 100;

    this.outerPosition = {
      h: h + '%',
      w: w + '%',
      l: (100 - w) / 2  + '%',
      t: (100 - h) / 2  + '%'
    };
  }

  public updateOpeningPositions(): void {
    const relativeToContainer = this.size;
    if (relativeToContainer) {
      this.openingsWithPosition = new Array<OpeningWithPosition>();
      this.project.openings.forEach( (opening: Opening) => {
        const openingWithPosition: OpeningWithPosition = opening as OpeningWithPosition;
        let openingSize = opening.size;
        if (opening.size.incomplete) {
          const pSize = this.project.size;
          if (pSize && !pSize.incomplete) {
            openingSize = Project.ensureRealSize(new Size(pSize.width * 0.5625, pSize.height * 0.65));
          } else {
            openingSize = Project.adjustSizeToRatio(Project.DEFAULTS.openingSize, opening.shape[0].aspectRatio);
          }
        }
        let h = relativeToContainer.height;
        let w = relativeToContainer.width;
        openingWithPosition.position = {
          h: openingSize.height / h * 100 + '%',
          w: openingSize.width / w * 100 + '%',
          t: opening.top ? (opening.top / h * 100 + '%') : (100 - (openingSize.height + this.project.offset * 2) / h * 100) / 2 + '%',
          b: opening.bottom ? (opening.bottom / h * 100 + '%') : (100 - (openingSize.height + this.project.offset * 2) / h * 100) / 2 + '%',
          l: opening.left ? (opening.left / w * 100 + '%') : (100 - openingSize.width / w * 100) / 2 + '%'
        };

        h = openingSize.height;
        w = openingSize.width;
        const vGrooveOffset = this.project.vGrooveOffset ? this.project.vGrooveOffset : Project.DEFAULTS.vGrooveOffset.openingEdgeMinOffset;
        const vGrooveSize = new Size(openingSize.width + vGrooveOffset * 2 + this.project.reveal * 2, openingSize.height + vGrooveOffset * 2 + this.project.reveal * 2);
        openingWithPosition.vGroovePosition = {
          h: vGrooveSize.height / h * 100 + '%',
          w: vGrooveSize.width / w * 100 + '%',
          t: opening.top ? (opening.top / h * 100 + '%') : (100 - vGrooveSize.height / h * 100) / 2 + '%',
          l: opening.left ? (opening.left / w * 100 + '%') : (100 - vGrooveSize.width / w * 100) / 2 + '%'
        };

        openingWithPosition.bigReveal = (this.project.reveal??0) / w > 0.1;
        this.openingsWithPosition.push(openingWithPosition);
      });
    }
  };

  public margins(opening: Opening):any {
    const relativeToContainer = this.size;
    const openingSize = opening.size.incomplete ? Project.adjustSizeToRatio(Project.DEFAULTS.openingSize, opening.shape[0].aspectRatio) : opening.size;
    if (relativeToContainer) {
      let h = relativeToContainer.height;
      let w = relativeToContainer.width;
      return {
        top: ((h - openingSize.height) / 2 - this.project.offset) / openingSize.height * 100 + '%',
        bottom: ((h - openingSize.height) / 2 + this.project.offset) / openingSize.height * 100 + '%',
        left: (w - openingSize.width) / 2 / openingSize.width * 100 + '%',
        right: (w - openingSize.width) / 2 / openingSize.width * 100 + '%'
      };
    }
  }

  public sizeOrDefault(opening: Opening): Size {
    return opening.size.incomplete ? Project.adjustSizeToRatio(Project.DEFAULTS.openingSize, opening.shape[0].aspectRatio) : opening.size;
  }

  public defaultVGrooveOffsets: SizeRange = new SizeRange(Project.DEFAULTS.vGrooveOffset.openingEdgeMinOffset,
                                                  Project.DEFAULTS.vGrooveOffset.openingEdgeMaxOffset,
                                                  Project.DEFAULTS.vGrooveOffset.vGrooveOffsetIncrement);

  public vGroove(): void {
    if (this.project.vGrooveOffset !== 0) {
      this.editingVGroove = true;
    } else {
      this.project.vGrooveOffset = this.project.availableVGrooveOffsets ? this.project.availableVGrooveOffsets.min : environment.vGroove.offset.openingEdgeMinOffset;
    }
  }

  public vGrooveInfo($event): void {
    $event.stopPropagation();
    const dialogRef = this.dialog.open(YesNoDialogComponent, {});
    dialogRef.componentInstance.data = {
      closeMethod: 'x',
      title: 'What is a V-Groove?',
      message: '<p>This refers to a shallow cut across the mat which exposes the core color of the primary mat. The standard size is typically 1/2" offset from the opening but can be customized if desired.</p>' +
        '<p class="image shadow"><img src="/assets/images/help/v-groove.jpg" alt="Matboard V-Groove"/></p>',
    };
  }

  overlaps:any = {
    controls: {
      width: false,
      height: false
    },
    opening: {
      width: false,
      height: false
    }
  }

  @ViewChild('container') container:ElementRef;
  private overlappingControls(dimension:string):boolean {
    if (!this.container || this.container.nativeElement.clientWidth == 0) {
      return false;
    }

    let pp = (o:string) => {
      return parseFloat(o.replace('%',''));
    }

    let size = this.container.nativeElement.clientWidth;

    // divide by 200 is really (/2 / 100) in case you are looking at this later and get confused
    if (dimension === 'height') {
      return pp(this.openingsWithPosition[0].position.l) / 100 * size < 42;
    } else {
      return pp(this.openingsWithPosition[0].position.t) / 100 * size < 30;
    }
  }

  private overlapsOpening(dimension:string):boolean {
    let prop = dimension.charAt(0).toLowerCase();
    let elProp = 'client' + prop.toUpperCase() + dimension.slice(1);

    if (!this.container || this.container.nativeElement[elProp] == 0) {
      return false;
    }

    let pp = (o:string) => {
      return parseFloat(o.replace('%',''));
    }

    let opening = this.openingsWithPosition[0].position;
    let previewPosition = this.previewPosition;

    return pp(opening[prop]) / 100 * this.container.nativeElement[elProp] * pp(previewPosition[prop]) / 100 < 60;
  }

  initialized:boolean = false;
  ngAfterViewInit() {
    // calculate overlaps whenever the project changes
    this.updateOuterPosition();
    this.updateOpeningPositions();
    this.calculateOverlaps();

    // changed after check error is ridiculous
    setTimeout( () => this.initialized = true, 0);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.projectChangeSubRef && !this.projectChangeSubRef.closed) {
      this.projectChangeSubRef.unsubscribe();
    }

    this.projectChangeSubRef = changes.project.currentValue.change.subscribe( () => {
      this.updateOuterPosition();
      this.updateOpeningPositions();
      this.calculateOverlaps();
    });
  }

  calculateOverlaps():void {
    // no point in doing thi sif we don't have an opening to cause overlap
    if (this.project.opening) {
      this.overlaps.controls.width = this.overlappingControls('width');
      this.overlaps.controls.height = this.overlappingControls('height');
      this.overlaps.opening.width = this.overlapsOpening('width');
      this.overlaps.opening.height = this.overlapsOpening('height');
    }
  }

  addOpening($event) {
    $event.stopPropagation();
    const openingSize = this.project.size.incomplete ? new Size(NaN, NaN) : Project.ensureRealSize(new Size(this.project.size.width * 0.5625, this.project.size.height * 0.65));
    const opening = new Opening(openingSize, [Shape.DEFAULT]);
    this.project.clearOpenings();
    this.project.addOpening(opening);
    this.project.bottomMargin = this.project.centeredMargins.height;
  }

  removeOpening($event, removedOpening: Opening) {
    $event.stopPropagation();
    $event.preventDefault();
    this.project.removeOpening(removedOpening);
  }

  cantRotateDialog(): void {
    const dialogRef = this.dialog.open(RotationViolationDialogComponent, {});
    dialogRef.componentInstance.project = this.project;
    dialogRef.afterClosed().subscribe(confirmRotate => {
      if (confirmRotate === true) {
        this.project.rotateOuter();
        this.project.rotateOpening();
      }
    });
  }

  rotateOpeningIfPossible($event) {
    $event.stopPropagation();
    $event.preventDefault();
    if (this.project.rotationRestrictions) {
      this.cantRotateDialog();
    } else {
      this.project.rotateOpening();
    }
  }

  rotateOuterIfPossible($event) {
    $event.stopPropagation();
    $event.preventDefault();
    if (this.project.rotationRestrictions) {
      this.cantRotateDialog();
    } else {
      this.project.rotateOuter();
    }
  }

  @Input()
  simpleMode:boolean = false;

  @Input()
  project: Project;

  @Output('editRequested')
  editRequested: EventEmitter<string> = new EventEmitter<string>();

  requestEdit($event, type:string):void {
    $event.stopPropagation();
    if (this.editingVGroove) {
      this.editingVGroove = false;
    } else {
      this.editRequested.emit(type);
    }
  }

  stopProp($event): void {
    if ($event.target.id !== 'click-pass-thru') {
      $event.stopPropagation();
    }
  }

  toggleMode($event) {
    $event.stopPropagation();
    this.simpleMode = !this.simpleMode;
  }

  openingSizeDimensionChanged($event: any, dim: string) {
    if ($event.newValue === 0) {
      const dialogRef = this.dialog.open(YesNoDialogComponent, {});
      dialogRef.componentInstance.data = {
        title: 'Remove Opening?',
        message: '<p>Remove opening and order a full sheet?</p>',
        yesButtonText: 'Yes',
        noButtonText: 'No'
      };

      dialogRef.afterClosed().subscribe(confirmDelete => {
        if (confirmDelete === true) {
          this.project.clearOpenings();
        } else {
          this.project.opening.size[dim] = $event.oldValue;
        }
      });
    }
  }

  printUploadCompleted(result: any, opening: Opening) {
    const print = {
      screenResUrl: result.screenResUrl,
      printResUrl: result.printResUrl,
      isUpload: true
    };
    if (opening) {
      opening.print = print;
    } else {
      this.project.print = print;
    }
  }

  printUploadFailed(err: any) {
    const dialogRef = this.dialog.open(YesNoDialogComponent, {});
    dialogRef.componentInstance.data = {
      closeMethod: 'x',
      title: 'Problem with Uploaded Image',
      yesButtonText: 'OK'
    };
    switch (err.ErrorCode) {
      case 'INVALID_IMAGE': dialogRef.componentInstance.data.message = '<p>An invalid image file was uploaded.  Please check your image and try again.</p>'; break;
      default: dialogRef.componentInstance.data.message = '<p>An unexpected error occurred: <strong>' + err.Message + '</strong></p>'; break;
    }
  }

  public removePrint(opening: Opening = null) {
    if (opening) {
      opening.print = null;
    } else if (this.project.print) {
      this.project.print = null;
    }
  }

  openZoomDialog() {
    const dialogRef = this.dialog.open(ZoomDialogComponent);
    dialogRef.componentInstance.imageUrl = this.project.url(1000, this.project.hasPrint ? null : 'checkered') + '&shadow=true';
  }

  public removeVGroove($event) {
    $event.stopPropagation();
    this.project.vGrooveOffset = 0;
    this.editingVGroove = false
  }

  public closeVGrooveEditWindow($event) {
    $event.stopPropagation();
    this.editingVGroove = false;
  }

  public ngOnDestroy() {
    if (this.projectChangeSubRef && !this.projectChangeSubRef.closed) {
      this.projectChangeSubRef.unsubscribe();
    }
  }

  public roundPreviewSize(w, closestWhole = 100) {
    return Math.round(w / closestWhole) * closestWhole;
  }
}
